import { Component, Fragment } from 'react';

import { Message } from 'semantic-ui-react';
import { Button, ButtonGroup, Modal, Link } from '@servicetitan/design-system';
import { observer } from 'mobx-react';
import { injectDependency } from '@servicetitan/react-ioc';
import {
    BankAccountModel,
    PlaidBankAccountVerificationStatuses,
} from '../api/internal-billing.api';
import { AchModalStore } from '../stores/ach-modal.store';
import { PlaidError, MetaData } from '../utils/plaid-integrations-service';
import * as Styles from './ach-modal.module.less';
import { Spinner, Stack } from '@servicetitan/design-system/dist/components';
import { Log } from '@servicetitan/log-service';

export interface ACHModalProp {
    buttonText?: string;
    onComplete(bankAccountDetails: BankAccountModel): void;
    isComplete?: boolean;
    isUpdate?: boolean;
    isPendingVerification?: boolean;
}

@observer
export class ACHModal extends Component<ACHModalProp> {
    @injectDependency(AchModalStore)
    declare private achModalStore: AchModalStore;
    @injectDependency(Log)
    declare private logService: Log;

    private onEvent = (eventName: string, metaData?: MetaData | undefined) => {
        this.logService.info({
            data: metaData,
            category: 'Plaid',
            code: 'jumpStart.onEvent',
            message: JSON.stringify(metaData),
        });
    };

    private onSuccess = async (id: string, metadata: any) => {
        let verificationStatus = undefined;
        switch (metadata.account.verification_status) {
            case 'manually_verified':
                verificationStatus = PlaidBankAccountVerificationStatuses.ManuallyVerified;
                break;
            case 'pending_manual_verification':
                verificationStatus = PlaidBankAccountVerificationStatuses.PendingManualVerification;
                break;
            case 'pending_automatic_verification':
                verificationStatus =
                    PlaidBankAccountVerificationStatuses.PendingAutomaticVerification;
                break;
        }

        this.logService.info({
            data: { metadata, id },
            category: 'Plaid',
            code: 'jumpStart.onSuccess',
            message: JSON.stringify({ id, metadata }),
        });

        try {
            const bankAccountDetails = await this.achModalStore.updateBankAccount(
                {
                    PublicToken: id,
                    PlaidAccountId: metadata.account_id,
                    InstitutionId: metadata.institution.institution_id,
                    VerificationStatus: verificationStatus,
                },
                {
                    onSuccess: this.onSuccess,
                    onExit: this.onExit,
                    onEvent: this.onEvent,
                }
            );
            await this.props.onComplete(bankAccountDetails!);
        } catch (e) {
            this.logService.error({
                error: JSON.stringify(e),
                category: 'Plaid',
                code: 'jumpStart.onSuccess.UpdateBankAccount',
                message: JSON.stringify(e),
            });
        }
    };

    private onSalesforceSuccess = async (messageValue: any) => {
        let verificationStatus = undefined;
        const data = messageValue.accounts[0];
        switch (data.verification_status) {
            case 'manually_verified':
                verificationStatus = PlaidBankAccountVerificationStatuses.ManuallyVerified;
                break;
            case 'pending_manual_verification':
                verificationStatus = PlaidBankAccountVerificationStatuses.PendingManualVerification;
                break;
            case 'pending_automatic_verification':
                verificationStatus =
                    PlaidBankAccountVerificationStatuses.PendingAutomaticVerification;
                break;
        }

        this.logService.info({
            data: messageValue,
            category: 'Plaid',
            code: 'jumpStart.sf.onSuccess',
            message: JSON.stringify(messageValue),
        });

        try {
            const bankAccountDetails = await this.achModalStore.updateBankAccount({
                PublicToken: '',
                PlaidAccountId: data.id,
                InstitutionId: messageValue.institution.institution_id,
                VerificationStatus: verificationStatus,
            });
            await this.props.onComplete(bankAccountDetails!);
        } catch (ex) {
            this.logService.error({
                error: JSON.stringify(ex),
                category: 'Plaid',
                code: 'jumpStart.sf.onSuccess.UpdateBankAccount',
                message: JSON.stringify(ex),
            });
        }
    };

    private onExit = (error: PlaidError | null, metadata?: MetaData) => {
        if (error?.error_code) {
            this.logService.error({
                error: error.error_message,
                category: 'Plaid',
                code: 'jumpStart.onExit',
                message: JSON.stringify({ error, metadata }),
            });
        } else {
            this.logService.info({
                data: metadata,
                category: 'Plaid',
                code: 'jumpStart.onExit',
                message: JSON.stringify(metadata),
            });
        }
        this.achModalStore.closeModal();
    };

    private destroyModal = () => {
        window.removeEventListener('message', this.salesForceIframeHandler);
    };

    private salesForceIframeHandler = (event: any) => {
        const { message, value } = event.data;
        const { salesForceIframeOrigin, closeModal, updateSFIframeVisibility, updateLoadingState } =
            this.achModalStore;
        if (
            event.origin !== salesForceIframeOrigin ||
            event.source !== (window.frames as any)['sf-ach-plaid-iframe']
        ) {
            return;
        }
        if (message === 'Loading') {
            updateLoadingState(false);
        }
        if (message === 'Exit' || message === 'EXIT') {
            closeModal();
            updateSFIframeVisibility(false);
        } else if (message === 'HANDOFF') {
            updateLoadingState(true);
        } else if (message === 'exception') {
            closeModal();
            updateSFIframeVisibility(false);
            this.logService.error({
                error: value,
                category: 'Salesforce error event',
                code: 'error_code',
                message: value,
            });
        } else if (message === 'Success') {
            this.onSalesforceSuccess(value);
        }
    };

    private retry = async () => {
        if (window.App.Features.SalesforceIntegration) {
            await this.achModalStore.initWithSalesforceFG();
        } else {
            await this.achModalStore.initialize({
                onSuccess: this.onSuccess,
                onExit: this.onExit,
                onEvent: this.onEvent,
            });
            this.achModalStore.openModal();
        }
    };

    componentDidMount() {
        if (!this.props.isComplete) {
            if (window.App.Features.SalesforceIntegration) {
                this.achModalStore.initWithSalesforceFG();
                window.addEventListener('message', this.salesForceIframeHandler);
            } else {
                this.achModalStore.initialize({
                    onSuccess: this.onSuccess,
                    onExit: this.onExit,
                    onEvent: this.onEvent,
                });
            }
        }
    }

    componentWillUnmount() {
        this.destroyModal();
    }

    render() {
        const { isComplete, buttonText, isPendingVerification } = this.props;
        const { isLoading } = this.achModalStore;

        const triggerAction = isPendingVerification
            ? this.achModalStore.execute({
                  onSuccess: this.onSuccess,
                  onExit: this.onExit,
                  onEvent: this.onEvent,
              })
            : this.achModalStore.openModal;
        const Trigger = () =>
            this.achModalStore.scriptFailure ? (
                <Fragment>
                    <Button
                        full
                        color="red"
                        onClick={this.retry}
                        disabled={isComplete || isLoading}
                        loading={isLoading}
                    >
                        Retry Connection
                    </Button>
                    <Message negative>
                        <p>Failed Connection to Payment gateway</p>
                    </Message>
                </Fragment>
            ) : !this.props.isUpdate ? (
                <Button
                    full
                    iconName="lock"
                    primary={!isComplete}
                    onClick={triggerAction}
                    disabled={isComplete || isLoading}
                    loading={isLoading}
                >
                    {buttonText}
                </Button>
            ) : (
                <Link subdued onClick={this.achModalStore.openModal}>
                    Update
                </Link>
            );

        const Actions = () => (
            <div style={{ float: 'right' }}>
                <ButtonGroup>
                    <Button onClick={this.achModalStore.closeModal} disabled={isLoading}>
                        Decline
                    </Button>
                    <Button
                        loading={this.achModalStore.isLoading}
                        primary
                        onClick={this.achModalStore.execute({
                            onSuccess: this.onSuccess,
                            onExit: this.onExit,
                            onEvent: this.onEvent,
                        })}
                        disabled={isLoading}
                    >
                        Accept
                    </Button>
                </ButtonGroup>
            </div>
        );

        return (
            <Fragment>
                <Trigger />
                <Modal
                    open={this.achModalStore.modalOpen}
                    size={Modal.Sizes.M}
                    title="Terms of service"
                    closable
                    onClose={this.achModalStore.closeModal}
                    footer={<Actions />}
                >
                    <p>
                        I hereby authorize SERVICETITAN, INC., herein called COMPANY, to initiate a
                        debit entry to my account in the amount indicated below, and to debit the
                        same to such account, as described herein and otherwise determined by
                        COMPANY in accordance with this authorization.
                    </p>

                    <p>
                        I also authorize recurring debits on a regular schedule for the payment of
                        recurring services provided by COMPANY.
                    </p>

                    <p>
                        For recurring payments, I authorize the following to be debited from my
                        account:
                    </p>

                    <p>
                        Amount Authorized: The agreed amount for your ServiceTitan subscription,
                        plus applicable taxes
                    </p>

                    <p>Number of Payments: Unlimited</p>

                    <p>Frequency and Timing of Payments: Per invoice</p>

                    <p>
                        I acknowledge that the origination of ACH transactions to my account must
                        comply with the provision of U.S. law and that I may only revoke this
                        authorization by notifying COMPANY as provided below. I further authorize
                        the origination of ACH transactions to correct over- or under-billing
                        related to changes in my account.
                    </p>

                    <p>
                        This authorization is to remain in full force and effect for the payment for
                        recurring service, until COMPANY has received written notification from me
                        of its termination in such time and in such manner as to afford COMPANY a
                        reasonable opportunity to act on it. If you believe any of the above
                        information to be in error or to contact the COMPANY for information on
                        revoking this authorization, please contact us at (818) 877-4675.
                    </p>
                </Modal>
                <Modal
                    size={Modal.Sizes.L}
                    open={this.achModalStore.isVisibleSFIframe}
                    scrollable={false}
                    className={Styles.sfModal}
                >
                    {isLoading && (
                        <Stack className="h-100" alignItems="center" justifyContent="center">
                            <Spinner size="small" />
                        </Stack>
                    )}
                    <iframe
                        style={{
                            height: '100%',
                            width: '100%',
                            display: isLoading ? 'none' : undefined,
                        }}
                        src={`${this.achModalStore.salesforceIframeSrc}${this.achModalStore.accountEmail}`}
                        name="sf-ach-plaid-iframe"
                    />
                </Modal>
            </Fragment>
        );
    }
}
